/**
 * OSMDEventStream
 *
 * An EventStream implementation that works with OSMD-rendered NoteContainers
 * instead of DOM elements. This provides visual feedback (coloring, pulsing)
 * for correct/incorrect note playing when using the OSMD Phaser backend.
 */

import { NoteEvent } from 'Models/EventStream';
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { MidiEventState, PrerenderedGraphics } from 'Types';
import { NoteContainer, NoteState } from '../../vexflow-phaser-backend/NoteContainer';
import { NotifyReact, NotifyReactEvents } from 'Phaser/EventBus2';
import { COLORBLIND_TYPE } from 'Utils/Constants';
import logger from 'Utils/Logger';

/**
 * Interface for getting NoteContainer instances by ID.
 */
export interface INoteContainerProvider {
  getNoteContainer(id: string): NoteContainer | undefined;
}

/**
 * Maps MidiEventState to NoteState for visual feedback.
 */
function _midiStateToNoteState(midiState: MidiEventState): NoteState {
  switch (midiState) {
    case MidiEventState.HIT:
      return 'correct';
    case MidiEventState.EARLY_ONSET:
    case MidiEventState.EARLY_OFFSET:
      return 'early';
    case MidiEventState.LATE_ONSET:
    case MidiEventState.LATE_OFFSET:
      return 'late';
    case MidiEventState.WRONG_NOTE:
    case MidiEventState.NEVER_PLAYED:
    case MidiEventState.ADDITIONAL_NOTE:
      return 'missed';
    default:
      return 'default';
  }
}

/**
 * EventStream implementation for OSMD Phaser backend.
 * Extends EventStreamPhaser2 for compatibility with CursorMobile.
 * Uses NoteContainer instances for visual feedback instead of DOM manipulation.
 */
export class OSMDEventStream extends EventStreamPhaser2 {
  private noteContainerProvider: INoteContainerProvider | null = null;

  /** Optional callback invoked whenever a note is marked correct (HIT).
   *  The scene uses this to detect the first correct note after a loop restart
   *  and trigger the ripple fade animation. */
  private onNoteMarkedCorrectCallback: (() => void) | null = null;

  /**
   * Set the provider for getting NoteContainer instances.
   * This should be called after the scene is initialized.
   */
  setNoteContainerProvider(provider: INoteContainerProvider): void {
    this.noteContainerProvider = provider;
  }

  /**
   * Register a callback that fires each time a note is marked correct.
   * Pass null to clear the callback.
   */
  setOnNoteMarkedCorrect(callback: (() => void) | null): void {
    this.onNoteMarkedCorrectCallback = callback;
  }

  /**
   * Get a NoteContainer by its VexFlow ID.
   */
  private getNoteContainer(id: string): NoteContainer | undefined {
    return this.noteContainerProvider?.getNoteContainer(id);
  }

  /**
   * Override to show feedback toast and apply visual state.
   */
  protected markOnsetAccuracyWithSideEffects(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number,
    pulse: boolean = false,
    midiNote: number | null,
  ): void {
    logger.debug('[OSMD-MIDI] OSMDEventStream.markOnsetAccuracyWithSideEffects', {
      eventState: MidiEventState[eventState],
      noteId: note.Graphics?.[0]?.staveNote?.id ?? 'no-graphics',
      graphicsCount: note.Graphics?.length ?? 0,
      timestamp,
      pulse,
      midiNote,
      hasProvider: this.noteContainerProvider !== null && this.noteContainerProvider !== undefined,
    });

    // Show feedback toast for timing errors
    switch (eventState) {
      case MidiEventState.EARLY_ONSET:
      case MidiEventState.LATE_ONSET:
      case MidiEventState.WRONG_NOTE:
        NotifyReactEvents.ShowFeedbackToast({ state: eventState });
        break;
    }

    // Call parent implementation
    super.markOnsetAccuracyWithSideEffects(note, eventState, timestamp, pulse, midiNote);
  }

  /**
   * Override to show feedback toast for offset timing errors.
   */
  protected markOffsetAccuracyWithSideEffects(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number | null,
  ): void {
    // Show feedback toast for timing errors
    switch (eventState) {
      case MidiEventState.EARLY_OFFSET:
      case MidiEventState.LATE_OFFSET:
        NotifyReactEvents.ShowFeedbackToast({ state: eventState });
        break;
    }

    // Call parent implementation
    super.markOffsetAccuracyWithSideEffects(note, eventState, timestamp);
  }

  /**
   * Override to pulse the NoteContainer instead of DOM element.
   */
  pulseMatchedNote(note: NoteEvent, _colorClass: string): void {
    if (note.Graphics.length === 0) {
      logger.debug('[OSMD-MIDI] OSMDEventStream.pulseMatchedNote — no graphics, skipping');
      return;
    }

    const noteId = note.Graphics[0].staveNote.id;
    const container = this.getNoteContainer(noteId);

    logger.debug('[OSMD-MIDI] OSMDEventStream.pulseMatchedNote', {
      noteId,
      containerFound: container !== undefined,
    });

    if (container) {
      // Pulse with colorblind-aware correct color (no color parameter = uses correct color)
      container.pulse(undefined, 750);
    }
  }

  /**
   * Override to set NoteContainer state for semi-errors (yellow).
   * No animation, just color change.
   */
  makeNoteSemiError = (graphics: PrerenderedGraphics[], cbType: COLORBLIND_TYPE | null): void => {
    for (const graphic of graphics) {
      const container = this.getNoteContainer(graphic.staveNote.id);
      if (container) {
        container.setState('late', cbType); // Semi-error state with colorblind support
      }
    }
  };

  /**
   * Override to set NoteContainer state for correct notes (green).
   */
  makeNoteCorrect = (graphics: PrerenderedGraphics[], cbType: COLORBLIND_TYPE | null): void => {
    logger.debug('[OSMD-MIDI] OSMDEventStream.makeNoteCorrect', {
      graphicsCount: graphics.length,
      ids: graphics.map((g) => g.staveNote.id),
    });
    for (const graphic of graphics) {
      const container = this.getNoteContainer(graphic.staveNote.id);
      if (container) {
        container.setState('correct', cbType); // Correct state with colorblind support
      } else {
        logger.debug(
          '[OSMD-MIDI] OSMDEventStream.makeNoteCorrect — container NOT found for id:',
          graphic.staveNote.id,
        );
      }
    }

    if (this.onNoteMarkedCorrectCallback) {
      this.onNoteMarkedCorrectCallback();
    }
  };

  /**
   * Override to set NoteContainer state for errors (red).
   * No animation, just color change.
   */
  makeNoteError = (graphics: PrerenderedGraphics[], cbType: COLORBLIND_TYPE | null): void => {
    logger.debug('[OSMD-MIDI] OSMDEventStream.makeNoteError', {
      graphicsCount: graphics.length,
      ids: graphics.map((g) => g.staveNote.id),
    });
    for (const graphic of graphics) {
      const container = this.getNoteContainer(graphic.staveNote.id);
      if (container) {
        container.setState('missed', cbType); // Error state with colorblind support
      } else {
        logger.debug(
          '[OSMD-MIDI] OSMDEventStream.makeNoteError — container NOT found for id:',
          graphic.staveNote.id,
        );
      }
    }
  };
}

/**
 * Factory function to create an OSMDEventStream with the same configuration
 * as the standard EventStreamPhaser2.
 */
export function createOSMDEventStream(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Phaser/codebase timeKeeper API
  timeKeeper: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Phaser/codebase offsets config type
  offsetsConfig: any,
  colorblindType: COLORBLIND_TYPE | null,
  weightedAccuracyDivisions: number = 1,
  weightedAccuracyHitVelocity: number = 0.025,
  weightedAccuracyMissVelocity: number = 0.1,
  useRepertoireMarkAccuracy: boolean = false,
): OSMDEventStream {
  return new OSMDEventStream(
    timeKeeper,
    offsetsConfig,
    colorblindType,
    weightedAccuracyDivisions,
    weightedAccuracyHitVelocity,
    weightedAccuracyMissVelocity,
    useRepertoireMarkAccuracy,
  );
}
